import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { getLessonColorScheme, getLessonIconScheme } from '@onesource/components';
import { type TSpaceBase } from '@onesource/schemas';
import { useGetLessons } from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import { MdArrowForward, MdLock } from 'react-icons/md';

interface ModulePreviewProps {
  module: TSpaceBase;
  parentSpace: TSpaceBase;
}

/**
 * Module Preview Component
 * Right panel showing lessons and videos preview for selected module
 * Based on Figma design
 */
export const ModulePreview = ({ module, parentSpace }: ModulePreviewProps) => {
  const navigate = useNavigate();
  const isLocked = module.isLocked ?? false;
  // Don't fetch lessons for a locked module — the API gates that route (403).
  const { data: lessons, isLoading } = useGetLessons(module.id, { enabled: !isLocked });

  const handleOpenModule = () => {
    navigate({ to: `/community-hub/spaces/${parentSpace.id}/modules/${module.id}` });
  };

  if (isLocked) {
    const lockMessage = module.prerequisiteName
      ? `Complete "${module.prerequisiteName}" to unlock this module.`
      : 'This module is locked.';
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          height: 300,
          textAlign: 'center',
          px: 3,
        }}
      >
        <MdLock size={40} color="#9e9e9e" />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {module.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {lockMessage}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, textAlign: 'right' }}>
        module preview
      </Typography>

      {/* Lessons Section */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        Lessons
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {lessons?.slice(0, 4).map((lesson, index) => {
          const colorSchemeColor = getLessonColorScheme(lesson.colorScheme).bgcolor;
          const LessonIcon = getLessonIconScheme(lesson.iconScheme);
          return (
            <Grid item xs={6} key={lesson.id}>
              <Box
                onClick={() =>
                  navigate({
                    to: `/community-hub/spaces/${parentSpace.id}/modules/${module.id}/lessons/${lesson.id}`,
                  })
                }
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '-2px -2px 6px rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    boxShadow: '-2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 8px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Left colored section with icon */}
                <Box
                  sx={{
                    width: 62,
                    bgcolor: colorSchemeColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <LessonIcon width={24} height={24} color="#333" />
                </Box>
                {/* Right cream section with text */}
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: '#fffbf8',
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  {module.showLessonLabel !== false && (
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '12px', lineHeight: 1.3 }}>
                      Lesson {index + 1}:
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: 1.4 }}>
                    {lesson.shortDescription || lesson.title}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
        {(!lessons || lessons.length === 0) && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              No lessons available
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Videos Section */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        Videos
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Video cards - placeholder for now */}
        <Grid item xs={6}>
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.300',
              height: 150,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Video Preview
            </Typography>
            {/* Video progress bar overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0,0,0,0.6)',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" color="white">
                0:51
              </Typography>
              <Box sx={{ flex: 1, height: 3, bgcolor: 'grey.500', borderRadius: 1 }}>
                <Box sx={{ width: '30%', height: '100%', bgcolor: 'primary.main', borderRadius: 1 }} />
              </Box>
              <Typography variant="caption" color="white">
                2:31
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Lorem ipsum sit amet
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Cardio
            </Typography>
            <Typography variant="caption" color="text.secondary">
              28 m
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.300',
              height: 150,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Video Preview
            </Typography>
            {/* Video progress bar overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0,0,0,0.6)',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" color="white">
                0:51
              </Typography>
              <Box sx={{ flex: 1, height: 3, bgcolor: 'grey.500', borderRadius: 1 }}>
                <Box sx={{ width: '30%', height: '100%', bgcolor: 'primary.main', borderRadius: 1 }} />
              </Box>
              <Typography variant="caption" color="white">
                2:31
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Lorem ipsum sit amet
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Cardio
            </Typography>
            <Typography variant="caption" color="text.secondary">
              28 m
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Open Module Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          endIcon={<MdArrowForward />}
          onClick={handleOpenModule}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
          }}
        >
          Open Module
        </Button>
      </Box>
    </Box>
  );
};
